import { Suspense, lazy, useEffect, type ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { PageContainer, Spinner } from '@castinflow/ui';
import { StatusPage } from './pages/StatusPage';
import { SignupPage } from './pages/SignupPage';
import { LoginPage } from './pages/LoginPage';
import { InviteAcceptPage } from './pages/InviteAcceptPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { HomePage } from './pages/HomePage';
import { OrgPage } from './pages/OrgPage';
import { SettingsTabsPage } from './pages/SettingsTabsPage';
import AdminHubPage from './pages/AdminHubPage';
import { NotFoundPage } from './pages/StatePages';
import { useSession } from './lib/session';
import { useScopeGuard } from './lib/scope';
import { AppLayout } from './shell/AppLayout';
import { RouteFocus } from './shell/route-focus';
import { PublicLayout } from './shell/PublicLayout';
import { ModuleRoute, PlanRoute } from './framework/plan/plan-context';
import { tr } from './i18n/tr';

/**
 * Tasarım kataloğu (WP 2.7 K-5): yalnız geliştirme/test derlemesinde veya açıkça `VITE_UI_CATALOG=1` ile. Üretim
 * derlemesinde `__UI_CATALOG__` sabit `false` olur ve dinamik içe aktarma pakete girmez (`scripts/check-web-bundle.mjs`).
 */
const UiCatalog = __UI_CATALOG__ ? lazy(() => import('./catalog/UiCatalog')) : null;

/** Kayıt çerçevesi (WP 2.8): rota düzeyinde tembel yükleme — liste/detay/form/seçici kodu ilk paketi büyütmez. */
const RecordListRoute = lazy(() =>
  import('./pages/RecordRoutes').then((m) => ({ default: m.RecordListRoute })),
);
const SearchPage = lazy(() =>
  import('./pages/SearchPage').then((m) => ({ default: m.SearchPage })),
);
/** WP 2.6: içe aktarma sihirbazı ve aktarım geçmişi (rota düzeyinde tembel) */
const ImportNewRoute = lazy(() =>
  import('./features/transfers/ImportWizardPage').then((m) => ({ default: m.ImportNewRoute })),
);
const ImportJobRoute = lazy(() =>
  import('./features/transfers/ImportWizardPage').then((m) => ({ default: m.ImportJobRoute })),
);
const TransfersPage = lazy(() =>
  import('./features/transfers/TransfersPage').then((m) => ({ default: m.TransfersPage })),
);
const ApprovalsPage = lazy(() => import('./framework/sales/ApprovalsPage'));
// v3.1 A5′: müşteri adayı eşleştirme kuyruğu (tembel parça)
const IdentityReviewsPage = lazy(() => import('./framework/sales/IdentityReviewsPage'));
const LeadComparePage = lazy(() => import('./framework/sales/LeadComparePage'));
const OpportunityBoard = lazy(() => import('./framework/sales/OpportunityBoard'));
const ProposalWizardPage = lazy(() => import('./framework/sales/ProposalWizardPage'));
const SalesSettingsPage = lazy(() => import('./framework/sales/SalesSettingsPage'));
// v4.6 satış potansiyeli: öncelikli takip, değerlendirme bekleyen adaylar, geciken takipler
const SalesFollowUpPage = lazy(() => import('./framework/potential/SalesFollowUpPage'));
const OrderNewRoute = lazy(() =>
  import('./framework/sales/OrderCreatePage').then((m) => ({ default: m.OrderNewRoute })),
);
const PurchaseOrderNewRoute = lazy(() =>
  import('./framework/sales/OrderCreatePage').then((m) => ({
    default: m.PurchaseOrderNewRoute,
  })),
);
// D1 (TI-022): Tedarikçiler çalışma alanı
const SuppliersPage = lazy(() =>
  import('./framework/purchasing/SuppliersPage').then((m) => ({ default: m.SuppliersPage })),
);
// G5: tedarik teklifleri ve muhasebe bağlantısı
const SupplierQuoteRequestsPage = lazy(() =>
  import('./framework/purchasing/SupplierQuotesPages').then((m) => ({
    default: m.SupplierQuoteRequestsPage,
  })),
);
const SupplierQuoteRequestPage = lazy(() =>
  import('./framework/purchasing/SupplierQuotesPages').then((m) => ({
    default: m.SupplierQuoteRequestPage,
  })),
);
const AccountingSourcesPage = lazy(() => import('./framework/purchasing/AccountingSourcesPage'));
const FeedPage = lazy(() => import('./framework/feed/FeedPage'));
// v3 C1: "Bugün" ekranı (kovalar + kayıt yan paneli)
const TodayPage = lazy(() => import('./framework/today/TodayPage'));
/** M3 K-M3-16: satış görünümleri ve ayarları */
const OpportunityOverviewPage = lazy(() =>
  import('./framework/sales/OpportunityViewsPages').then((m) => ({
    default: m.OpportunityOverviewPage,
  })),
);
const OpportunityForecastPage = lazy(() =>
  import('./framework/sales/OpportunityViewsPages').then((m) => ({
    default: m.OpportunityForecastPage,
  })),
);
const OpportunityTeamPage = lazy(() =>
  import('./framework/sales/OpportunityViewsPages').then((m) => ({
    default: m.OpportunityTeamPage,
  })),
);
const OpportunitySettingsPage = lazy(() => import('./framework/sales/OpportunitySettingsPage'));
const LeadOverviewPage = lazy(() => import('./framework/sales/LeadOverviewPage'));
const LeadSettingsPage = lazy(() => import('./framework/sales/LeadSettingsPage'));
// v3.1 S9: müşteri kazanımı (aday merkezi, dağıtım, kampanya raporu, uçlar, kaynak ve kampanyalar)
const LeadCenterPage = lazy(() => import('./framework/acquisition/LeadCenterPage'));
const DistributionQueuePage = lazy(() => import('./framework/acquisition/DistributionQueuePage'));
const CampaignReportPage = lazy(() => import('./framework/acquisition/CampaignReportPage'));
const MarketingWorkcenterPage = lazy(() => import('./framework/marketing/MarketingWorkcenterPage'));
// E2: segmentler, yeniden kazanım, kampanya–görüşme–satış kalitesi
const SegmentsPage = lazy(() => import('./framework/marketing/SegmentsPage'));
const RewinPage = lazy(() => import('./framework/marketing/RewinPage'));
const QualityPage = lazy(() => import('./framework/marketing/QualityPage'));
/** v3.1 S11: reklam harcaması (salt okuma) ve reklam değişikliği istekleri (reklam hesabına yazma yok) */
const AdSpendPage = lazy(() => import('./framework/ads/AdSpendPage'));
const AdChangeRequestsPage = lazy(() => import('./framework/ads/AdChangeRequestsPage'));
// v4.5 B-1a/B-9: proje ve hizmet teslimi (modül `delivery`; kapalı modülde rota açıklama gösterir)
const DeliveryListPage = lazy(() => import('./framework/delivery/DeliveryListPage'));
const DeliveryCreatePage = lazy(() => import('./framework/delivery/DeliveryCreatePage'));
const DeliveryDetailPage = lazy(() => import('./framework/delivery/DeliveryDetailPage'));
// TI-039: müşteri iş görünümü (aynı müşterinin projeleri, hizmetleri, teslimleri, destek geçmişi ve portal görünümü)
const DeliveryCustomerPage = lazy(() => import('./framework/delivery/DeliveryCustomerPage'));
// G6 / K-3: yaklaşan yenilemeler (müşteri hakları; sayı = liste)
const RenewalsPage = lazy(() => import('./framework/delivery/delivery-entitlements'));
const LeadEndpointsPage = lazy(() => import('./framework/acquisition/LeadEndpointsPage'));
const DistributionRulesPage = lazy(() => import('./framework/acquisition/DistributionRulesPage'));
const AcquisitionSettingsPage = lazy(
  () => import('./framework/acquisition/AcquisitionSettingsPage'),
);
const LeadSequenceBoardPage = lazy(() => import('./framework/sales/LeadSequenceViewsPages'));
const LeadSequenceNextStepsPage = lazy(() =>
  import('./framework/sales/LeadSequenceViewsPages').then((m) => ({
    default: m.LeadSequenceNextStepsPage,
  })),
);
const LeadSequenceReportPage = lazy(() =>
  import('./framework/sales/LeadSequenceViewsPages').then((m) => ({
    default: m.LeadSequenceReportPage,
  })),
);
const OrderBoardPage = lazy(() => import('./framework/sales/OrderBoardPage'));
// M4 WP 4.2: aktivite görünümleri ve ayarları (tembel parçalar)
const ActivityBoardPage = lazy(() => import('./framework/activities/ActivityBoardPage'));
const ActivityCalendarPage = lazy(() => import('./framework/activities/ActivityCalendarPage'));
const ActivityMapPage = lazy(() => import('./framework/activities/ActivityMapPage'));
const ActivitySettingsPage = lazy(() => import('./framework/activities/ActivitySettingsPage'));
const FeedSettingsPage = lazy(() => import('./framework/feed/FeedSettingsPage'));
const WorkCalendarPage = lazy(() => import('./framework/settings/WorkCalendarPage'));
// v3.1 A1: Şirketim (tenant ayarları formu)
const CompanySettingsPage = lazy(() => import('./framework/settings/CompanySettingsPage'));
/** M4 WP 4.4: destek panosu (KPI; kendi SVG grafikleri) */
const SupportOverviewPage = lazy(() => import('./framework/support/SupportOverviewPage'));
const PublicLinksPage = lazy(() => import('./framework/settings/PublicLinksPage'));
/** M5 WP 5.5: web kancaları (Kontrol paneli) ve kullanıcı kaydı › API anahtarı */
const WebhooksPage = lazy(() => import('./framework/integrations/WebhooksPage'));
const UserRecordPage = lazy(() => import('./framework/integrations/UserRecordPage'));
// v3.1 A3: ayrılan kullanıcının kayıt devri sihirbazı
const HandoverWizardPage = lazy(() => import('./framework/handover/HandoverWizardPage'));
/** M6 WP 6.6: bekleyen dış etkili işler (geri yükleme sonrası karantina incelemesi) */
const QuarantinedJobsPage = lazy(() => import('./framework/quarantine/QuarantinedJobsPage'));
/** M5 WP 5.1: formlar (galeri + tanımlı formlar, tasarımcı, form belgeleri) */
const FormsHomePage = lazy(() => import('./framework/forms/FormsHomePage'));
const FormBuilderPage = lazy(() => import('./framework/forms/FormBuilderPage'));
const FormDocumentsPage = lazy(() => import('./framework/forms/FormDocumentListPage'));
/** M5 WP 5.4: e-posta yönetimi (SMTP, şablonlar, gönderim kaydı) ve e-postadaki imzalı kayıt bağlantısı */
const EmailSettingsPage = lazy(() => import('./framework/email/EmailSettingsPage'));
/** M5 WP 5.3: oyunlaştırma — kullanıcı ekranı (puan, sıralama, rozetler) ve yönetim (ayarlar, kurallar, oyunlar, rozetler) */
const GamificationPage = lazy(() => import('./framework/gamification/GamificationPage'));
const GamificationSettingsPage = lazy(
  () => import('./framework/gamification/GamificationSettingsPage'),
);
const SmartLinkPage = lazy(() => import('./framework/email/SmartLinkPage'));
const InboundEmailPage = lazy(() => import('./framework/inbound/InboundEmailPage'));
/** M6 WP 6.1: plan ve kullanım (yalnız bilgilendirme; plan bu ekrandan değişmez) */
const PlanPage = lazy(() => import('./framework/plan/PlanPage'));
/** M6 WP 6.4: veri yaşam döngüsü, kişisel veri işlemleri, denetim kayıtları, destek erişimi, kapatma iptali (oturumsuz) */
const DataLifecyclePage = lazy(() => import('./framework/lifecycle/DataLifecyclePage'));
const SubjectDataPage = lazy(() => import('./framework/lifecycle/SubjectDataPage'));
const AuditLogPage = lazy(() => import('./framework/lifecycle/AuditLogPage'));
const SupportAccessPage = lazy(() => import('./framework/lifecycle/SupportAccessPage'));
const ClosureCancelPage = lazy(() => import('./framework/lifecycle/ClosureCancelPage'));
const ClosureExportPage = lazy(() => import('./framework/lifecycle/ClosureExportPage'));
/** M6 WP 6.2: hesap ve güvenlik (kullanıcı), IP izin listesi, görünüm ve davranış, entegrasyon kataloğu (yönetim) */
const AccountPage = lazy(() => import('./framework/account/AccountPage'));
const IpAllowlistPage = lazy(() => import('./framework/settings/IpAllowlistPage'));
const AppearanceSettingsPage = lazy(() => import('./framework/settings/AppearanceSettingsPage'));
const IntegrationsPage = lazy(() => import('./framework/settings/IntegrationsPage'));
/** EK1: ajan profilleri, bağlı uygulama kurulumları ve demo sınaması (Yönetim › Ajanlar) */
const AgentsPage = lazy(() => import('./framework/agents/AgentsPage'));
const ConnectionsPage = lazy(() => import('./framework/connections/ConnectionsPage'));
/** v3.1 CA-4: reklam hesabı bağlama — onay ekranından dönüş sayfası (GET yan etkisiz; kod API'ye POST edilir) */
const AdConnectCallbackPage = lazy(() => import('./framework/connections/AdConnectCallbackPage'));
/** v3.2 D: arama reklamları hesabı bağlama — onay ekranından dönüş sayfası (GET yan etkisiz; kod API'ye POST edilir) */
const GoogleAdsConnectCallbackPage = lazy(
  () => import('./framework/connections/GoogleAdsConnectCallbackPage'),
);
// v3.2 görev F: iş mesajlaşma hesabı gömülü kaydı (üçüncü taraf kitaplığı yalnız bu yüzeyde, açık istekle yüklenir)
const WhatsAppSignupPage = lazy(() => import('./framework/connections/WhatsAppSignupPage'));
/** v3.1 S10: ekip gelen kutusu, iletişim kanalları, hazır yanıtlar */
const InboxPage = lazy(() => import('./framework/inbox/InboxPage'));
const ChannelsPage = lazy(() => import('./framework/inbox/ChannelsPage'));
const QuickRepliesPage = lazy(() => import('./framework/inbox/QuickRepliesPage'));
/** M4 WP 4.5: performans monitörü, hedefler, faaliyet raporu, Excel göstergesi */
const DashboardPage = lazy(() => import('./framework/performance/DashboardPage'));
const GoalsPage = lazy(() => import('./framework/performance/GoalsPage'));
const ActivityReportPage = lazy(() => import('./framework/performance/ActivityReportPage'));
const ExcelSourcesPage = lazy(() => import('./framework/performance/ExcelSourcesPage'));
/** M5 WP 5.2: otomasyon (liste, oluşturucu, çalışma geçmişi) */
const AutomationListPage = lazy(() => import('./framework/automation/AutomationListPage'));
const AutomationBuilderPage = lazy(() => import('./framework/automation/AutomationBuilderPage'));
const AutomationRunsPage = lazy(() => import('./framework/automation/AutomationRunsPage'));
const RecordDetailRoute = lazy(() =>
  import('./pages/RecordRoutes').then((m) => ({ default: m.RecordDetailRoute })),
);

function RouteLoading() {
  return (
    <div className="flex justify-center p-8" data-testid="route-loading">
      <Spinner label={tr.shell.checkingSession} />
    </div>
  );
}

/**
 * Kendi sayfa kapsayıcısı olmayan tembel sayfalar: standart kenar boşluğu ve içerik genişliği (PageContainer). Bu sayfalar
 * önceden kabuğun ana alanına yapışık (sol menü kenarında, üst boşluksuz) çiziliyordu.
 */
function PaddedRoute({ children }: { children: ReactNode }) {
  return (
    <Suspense fallback={<RouteLoading />}>
      <PageContainer>{children}</PageContainer>
    </Suspense>
  );
}

function SessionPending() {
  return (
    <div
      className="flex min-h-screen items-center justify-center bg-canvas"
      data-testid="session-pending"
    >
      <Spinner label={tr.shell.checkingSession} />
    </div>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const status = useSession((s) => s.status);
  if (status === 'unknown') return <SessionPending />;
  if (status === 'anonymous') return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/** Giriş/kayıt: oturum açıksa uygulamaya yönlenir. */
function PublicOnly({ children, wide = false }: { children: ReactNode; wide?: boolean }) {
  const status = useSession((s) => s.status);
  if (status === 'unknown') return <SessionPending />;
  if (status === 'authenticated') return <Navigate to="/" replace />;
  return <PublicLayout wide={wide}>{children}</PublicLayout>;
}

/** Sistem durumu hem oturum içinde (kabukta) hem dışında açılır. */
function StatusRoute() {
  const status = useSession((s) => s.status);
  if (status === 'unknown') return <SessionPending />;
  if (status === 'authenticated')
    return (
      <AppLayout>
        <PageContainer width="form">
          <StatusPage />
        </PageContainer>
      </AppLayout>
    );
  return (
    <PublicLayout wide>
      <StatusPage />
    </PublicLayout>
  );
}

export function App() {
  const load = useSession((s) => s.load);
  const refresh = useSession((s) => s.refresh);
  // Kapsam (tenant/kullanıcı) değişince bellek önbelleği temizlenir (WP 2.8 §29)
  useScopeGuard();

  useEffect(() => {
    void load();
  }, [load]);

  // TB-1: yetenekler sunucuda anında değişebilir; sekme yeniden görünür olunca `/auth/me` tazelenir (menü/eylem
  // görünürlüğü sunucu sonucuyla hizalı kalır — güvenlik sınırı yine API'dedir)
  useEffect(() => {
    const on = () => {
      if (document.visibilityState === 'visible') void refresh();
    };
    document.addEventListener('visibilitychange', on);
    return () => {
      document.removeEventListener('visibilitychange', on);
    };
  }, [refresh]);

  return (
    <>
      <RouteFocus />
      <Routes>
        <Route
          path="/login"
          element={
            <PublicOnly>
              <LoginPage />
            </PublicOnly>
          }
        />
        <Route
          path="/signup"
          element={
            <PublicOnly wide>
              <SignupPage />
            </PublicOnly>
          }
        />
        <Route
          path="/closure-cancel"
          element={
            <PublicOnly>
              <Suspense fallback={<SessionPending />}>
                <ClosureCancelPage />
              </Suspense>
            </PublicOnly>
          }
        />
        {/* v3.1 A8: kapatma sürecinde (dondurmada giriş kapalı) çalışma alanı paketi — sahip doğrulamasıyla */}
        <Route
          path="/closure-export"
          element={
            <PublicOnly>
              <Suspense fallback={<SessionPending />}>
                <ClosureExportPage />
              </Suspense>
            </PublicOnly>
          }
        />
        {/* M7 T2: e-postadaki bağlantıyla açılan sayfalar ve "parolamı unuttum". Oturumsuz; oturum açıkken de AÇILIR
            (yönlendirme adresteki belirteci düşürürdü) ve oturum denetiminin bitmesi beklenmez. */}
        <Route
          path="/invite"
          element={
            <PublicLayout>
              <InviteAcceptPage />
            </PublicLayout>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicLayout>
              <ResetPasswordPage />
            </PublicLayout>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicLayout>
              <ForgotPasswordPage />
            </PublicLayout>
          }
        />
        <Route path="/status" element={<StatusRoute />} />
        {UiCatalog ? (
          <Route
            path="/_ui/*"
            element={
              <Suspense fallback={<SessionPending />}>
                <UiCatalog />
              </Suspense>
            }
          />
        ) : null}
        <Route
          element={
            <RequireAuth>
              <AppLayout />
            </RequireAuth>
          }
        >
          <Route index element={<HomePage />} />
          <Route path="/org" element={<OrgPage />} />
          <Route path="/settings" element={<SettingsTabsPage />} />
          {/* v3.9 Yönetim merkezi: arama + beş kategori; kategori sayfası yalnız o kategorinin ayarlarını listeler */}
          <Route path="/yonetim" element={<AdminHubPage />} />
          <Route path="/yonetim/:kategori" element={<AdminHubPage />} />
          <Route
            path="/settings/plan"
            element={
              <PaddedRoute>
                <PlanPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/settings/data-lifecycle"
            element={
              <PaddedRoute>
                <DataLifecyclePage />
              </PaddedRoute>
            }
          />
          <Route
            path="/account"
            element={
              <PaddedRoute>
                <AccountPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/settings/data-lifecycle/subject/:type/:id"
            element={
              <PaddedRoute>
                <SubjectDataPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/settings/security"
            element={
              <PaddedRoute>
                <IpAllowlistPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/settings/audit"
            element={
              <PaddedRoute>
                <AuditLogPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/settings/appearance"
            element={
              <PaddedRoute>
                <AppearanceSettingsPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/settings/support-access"
            element={
              <PaddedRoute>
                <SupportAccessPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/settings/integrations"
            element={
              <PaddedRoute>
                <IntegrationsPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/settings/agents"
            element={
              <PaddedRoute>
                <AgentsPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/settings/connections"
            element={
              <PaddedRoute>
                <ConnectionsPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/settings/connections/ads/callback"
            element={
              <PaddedRoute>
                <AdConnectCallbackPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/settings/connections/ads/google/callback"
            element={
              <PaddedRoute>
                <GoogleAdsConnectCallbackPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/settings/connections/whatsapp/signup"
            element={
              <PaddedRoute>
                <WhatsAppSignupPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/settings/channels"
            element={
              <PaddedRoute>
                <ChannelsPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/settings/lead-endpoints"
            element={
              <PaddedRoute>
                <LeadEndpointsPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/settings/quick-replies"
            element={
              <PaddedRoute>
                <QuickRepliesPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/settings/distribution-rules"
            element={
              <PaddedRoute>
                <DistributionRulesPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/settings/acquisition"
            element={
              <PaddedRoute>
                <AcquisitionSettingsPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/settings/work-calendar"
            element={
              <PaddedRoute>
                <WorkCalendarPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/settings/company"
            element={
              <PaddedRoute>
                <CompanySettingsPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/settings/public-links"
            element={
              <PaddedRoute>
                <PublicLinksPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/settings/webhooks"
            element={
              <PaddedRoute>
                <WebhooksPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/settings/accounting"
            element={
              <PaddedRoute>
                <AccountingSourcesPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/settings/quarantined-jobs"
            element={
              <PaddedRoute>
                <QuarantinedJobsPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/settings/email"
            element={
              <PaddedRoute>
                <EmailSettingsPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/settings/gamification"
            element={
              <PaddedRoute>
                <GamificationSettingsPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/gamification"
            element={
              <PaddedRoute>
                <GamificationPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/settings/inbound-email"
            element={
              <PaddedRoute>
                <InboundEmailPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/users/:id"
            element={
              <PaddedRoute>
                <UserRecordPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/users/:id/handover"
            element={
              <Suspense fallback={<RouteLoading />}>
                <HandoverWizardPage />
              </Suspense>
            }
          />
          <Route
            path="/l/:token"
            element={
              <PaddedRoute>
                <SmartLinkPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/automation"
            element={
              <Suspense fallback={<RouteLoading />}>
                <AutomationListPage />
              </Suspense>
            }
          />
          <Route
            path="/automation/new"
            element={
              <Suspense fallback={<RouteLoading />}>
                <PlanRoute feature="automation" padded>
                  <AutomationBuilderPage />
                </PlanRoute>
              </Suspense>
            }
          />
          <Route
            path="/automation/:id"
            element={
              <Suspense fallback={<RouteLoading />}>
                <AutomationBuilderPage />
              </Suspense>
            }
          />
          <Route
            path="/automation/:id/runs"
            element={
              <Suspense fallback={<RouteLoading />}>
                <AutomationRunsPage />
              </Suspense>
            }
          />
          <Route
            path="/settings/feed"
            element={
              <PaddedRoute>
                <FeedSettingsPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/search"
            element={
              <Suspense fallback={<RouteLoading />}>
                <SearchPage />
              </Suspense>
            }
          />
          <Route
            path="/records/:type"
            element={
              <Suspense fallback={<RouteLoading />}>
                <RecordListRoute />
              </Suspense>
            }
          />
          <Route
            path="/records/:type/:id"
            element={
              <Suspense fallback={<RouteLoading />}>
                <RecordDetailRoute />
              </Suspense>
            }
          />
          <Route
            path="/imports/new/:type"
            element={
              <Suspense fallback={<RouteLoading />}>
                <ImportNewRoute />
              </Suspense>
            }
          />
          <Route
            path="/imports/:id"
            element={
              <Suspense fallback={<RouteLoading />}>
                <ImportJobRoute />
              </Suspense>
            }
          />
          <Route
            path="/feed"
            element={
              <PaddedRoute>
                <FeedPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/today"
            element={
              <PaddedRoute>
                <TodayPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/inbox"
            element={
              <PaddedRoute>
                <InboxPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/sales/approvals"
            element={
              <PaddedRoute>
                <ApprovalsPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/sales/identity-reviews"
            element={
              <PaddedRoute>
                <IdentityReviewsPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/sales/identity-reviews/karsilastir"
            element={
              <PaddedRoute>
                <LeadComparePage />
              </PaddedRoute>
            }
          />
          <Route
            path="/sales/opportunities/board"
            element={
              <PaddedRoute>
                <OpportunityBoard />
              </PaddedRoute>
            }
          />
          <Route
            path="/sales/proposals/wizard"
            element={
              <Suspense fallback={<RouteLoading />}>
                <ProposalWizardPage />
              </Suspense>
            }
          />
          <Route
            path="/sales/orders/new"
            element={
              <Suspense fallback={<RouteLoading />}>
                <OrderNewRoute />
              </Suspense>
            }
          />
          <Route
            path="/sales/purchase-orders/new"
            element={
              <Suspense fallback={<RouteLoading />}>
                <PurchaseOrderNewRoute />
              </Suspense>
            }
          />
          <Route
            path="/sales/suppliers"
            element={
              <Suspense fallback={<RouteLoading />}>
                <SuppliersPage />
              </Suspense>
            }
          />
          <Route
            path="/sales/suppliers/quotes"
            element={
              <PaddedRoute>
                <SupplierQuoteRequestsPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/sales/suppliers/quotes/:id"
            element={
              <PaddedRoute>
                <SupplierQuoteRequestPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/sales/opportunities/overview"
            element={
              <PaddedRoute>
                <OpportunityOverviewPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/sales/opportunities/forecast"
            element={
              <PaddedRoute>
                <OpportunityForecastPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/sales/opportunities/team"
            element={
              <PaddedRoute>
                <OpportunityTeamPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/sales/settings/opportunities"
            element={
              <PaddedRoute>
                <OpportunitySettingsPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/sales/leads/overview"
            element={
              <PaddedRoute>
                <LeadOverviewPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/sales/leads/center"
            element={
              <PaddedRoute>
                <LeadCenterPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/sales/leads/distribution"
            element={
              <PaddedRoute>
                <DistributionQueuePage />
              </PaddedRoute>
            }
          />
          <Route
            path="/marketing"
            element={
              <PaddedRoute>
                <MarketingWorkcenterPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/marketing/segments"
            element={
              <PaddedRoute>
                <SegmentsPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/marketing/rewin"
            element={
              <PaddedRoute>
                <RewinPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/marketing/quality"
            element={
              <PaddedRoute>
                <QualityPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/sales/leads/campaign-report"
            element={
              <PaddedRoute>
                <CampaignReportPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/ads/spend"
            element={
              <PaddedRoute>
                <AdSpendPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/ads/requests"
            element={
              <PaddedRoute>
                <AdChangeRequestsPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/teslim"
            element={
              <PaddedRoute>
                <ModuleRoute
                  module="delivery"
                  testId="delivery-list-page"
                  readOnlyTestId="teslim-salt-okunur"
                >
                  <DeliveryListPage />
                </ModuleRoute>
              </PaddedRoute>
            }
          />
          <Route
            path="/teslim/yeni"
            element={
              <PaddedRoute>
                <ModuleRoute
                  module="delivery"
                  testId="delivery-create-page"
                  readOnlyTestId="teslim-salt-okunur"
                >
                  <DeliveryCreatePage />
                </ModuleRoute>
              </PaddedRoute>
            }
          />
          <Route
            path="/teslim/musteri/:companyId"
            element={
              <PaddedRoute>
                <ModuleRoute
                  module="delivery"
                  testId="delivery-customer-page"
                  readOnlyTestId="teslim-salt-okunur"
                >
                  <DeliveryCustomerPage />
                </ModuleRoute>
              </PaddedRoute>
            }
          />
          <Route
            path="/teslim/yenilemeler"
            element={
              <PaddedRoute>
                <ModuleRoute
                  module="delivery"
                  testId="delivery-renewals-page"
                  readOnlyTestId="teslim-salt-okunur"
                >
                  <RenewalsPage />
                </ModuleRoute>
              </PaddedRoute>
            }
          />
          <Route
            path="/teslim/:id"
            element={
              <PaddedRoute>
                <ModuleRoute
                  module="delivery"
                  testId="delivery-detail-page"
                  readOnlyTestId="teslim-salt-okunur"
                >
                  <DeliveryDetailPage />
                </ModuleRoute>
              </PaddedRoute>
            }
          />
          <Route
            path="/sales/settings/leads"
            element={
              <PaddedRoute>
                <LeadSettingsPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/sales/leads/sequences"
            element={
              <PaddedRoute>
                <LeadSequenceBoardPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/sales/leads/sequences/next-steps"
            element={
              <PaddedRoute>
                <LeadSequenceNextStepsPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/sales/leads/sequences/report"
            element={
              <PaddedRoute>
                <LeadSequenceReportPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <PaddedRoute>
                <DashboardPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/dashboard/activity-report"
            element={
              <PaddedRoute>
                <ActivityReportPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/dashboard/excel"
            element={
              <PaddedRoute>
                <ExcelSourcesPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/goals"
            element={
              <PaddedRoute>
                <GoalsPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/support/overview"
            element={
              <PaddedRoute>
                <SupportOverviewPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/forms"
            element={
              <PaddedRoute>
                <FormsHomePage />
              </PaddedRoute>
            }
          />
          <Route
            path="/forms/yeni"
            element={
              <PaddedRoute>
                <PlanRoute feature="custom_forms">
                  <FormBuilderPage />
                </PlanRoute>
              </PaddedRoute>
            }
          />
          <Route
            path="/forms/:id/duzenle"
            element={
              <PaddedRoute>
                <FormBuilderPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/forms/:id"
            element={
              <Suspense fallback={<RouteLoading />}>
                <FormDocumentsPage />
              </Suspense>
            }
          />
          <Route
            path="/sales/orders/board"
            element={
              <PaddedRoute>
                <OrderBoardPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/activities/board"
            element={
              <PaddedRoute>
                <ActivityBoardPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/activities/calendar"
            element={
              <PaddedRoute>
                <ActivityCalendarPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/activities/map"
            element={
              <PaddedRoute>
                <ActivityMapPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/activities/settings"
            element={
              <PaddedRoute>
                <ActivitySettingsPage />
              </PaddedRoute>
            }
          />
          <Route
            path="/sales/settings"
            element={
              <Suspense fallback={<RouteLoading />}>
                <SalesSettingsPage />
              </Suspense>
            }
          />
          <Route
            path="/sales/follow-up"
            element={
              <Suspense fallback={<RouteLoading />}>
                <SalesFollowUpPage />
              </Suspense>
            }
          />
          <Route
            path="/transfers"
            element={
              <Suspense fallback={<RouteLoading />}>
                <TransfersPage />
              </Suspense>
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </>
  );
}
